/**
 * Best-effort PWA / desktop-browser shell.
 *
 * Lets the whole app run (and be developed) without any native wrapper. It is
 * intentionally "somewhat lacking" — that's fine, it's a fallback:
 *   • speech     → Web Speech API (online, no real grammar constraint)
 *   • location   → navigator.geolocation, heading derived from movement
 *   • keepAwake  → Screen Wake Lock API (screen must stay on)
 *   • storage    → localStorage
 *
 * A separate automated-test shell can implement NativeShell directly and set
 * window.__wildhunt_shell before app.js loads; it does not go through here.
 */
import type { NativeShell, NativeEvent, ShellCapabilities, GeoFix } from './types';

const HEADING_DISTANCE_M = 20;
const HEADING_HISTORY_MS = 60_000;

// ── Minimal typings for non-standard / unstable Web APIs ────────────────────
// lib.dom lacks stable defs for these, so we declare the small subset we use
// instead of leaning on `any` everywhere.
interface SpeechRecognitionAlt {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0?: SpeechRecognitionAlt;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionErrorEventLike {
  error: string;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', cb: () => void): void;
}
interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

interface PwaWindow extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function distM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6_371_000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function bearingDeg(fromLat: number, fromLng: number, toLat: number, toLng: number): number {
  const dLng = ((toLng - fromLng) * Math.PI) / 180;
  const l1 = (fromLat * Math.PI) / 180;
  const l2 = (toLat * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(l2);
  const x = Math.cos(l1) * Math.sin(l2) - Math.sin(l1) * Math.cos(l2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function createPwaShell(): NativeShell {
  const handlers = new Set<(e: NativeEvent) => void>();
  const emit = (e: NativeEvent) => {
    for (const h of handlers) h(e);
  };

  const win = window as PwaWindow;
  const SR: SpeechRecognitionCtor | undefined = win.SpeechRecognition || win.webkitSpeechRecognition;
  const geo = navigator.geolocation;

  const caps: ShellCapabilities = {
    speech: false, // Web Speech API is too unreliable in browsers; tap-only
    location: !!geo,
    keepAwake: 'wakeLock' in navigator,
    storage: true,
    platform: 'pwa',
  };

  // ── Speech (Web Speech API) ────────────────────────────────────────────
  let recog: SpeechRecognitionLike | null = null;
  let grammar: string[] = [];
  let speechWanted = false;
  // Kept across restarts so speechReady fires only once per startSpeech() call.
  let readyEmitted = false;

  function startRecog() {
    if (!SR || !speechWanted) return;
    const r = new SR();
    recog = r;
    r.continuous = true;
    r.interimResults = false;
    r.lang = 'en-US';
    r.onstart = () => {
      if (!readyEmitted) {
        readyEmitted = true;
        emit({ type: 'speechReady' });
      }
    };
    r.onresult = (ev: SpeechRecognitionEventLike) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (!res.isFinal) continue;
        const text = (res[0]?.transcript || '').toLowerCase().trim();
        // Cheap grammar match: emit the first grammar word found in the phrase.
        const hit = grammar.find((w) => text.split(/\s+/).includes(w));
        if (hit) emit({ type: 'speechCommand', word: hit });
      }
    };
    r.onerror = (ev: SpeechRecognitionErrorEventLike) => {
      if (ev.error === 'no-speech' || ev.error === 'aborted') return;
      // Permanent failures: stop the restart loop so the recording indicator
      // doesn't flash forever.
      if (ev.error === 'not-allowed' || ev.error === 'audio-capture' || ev.error === 'service-not-allowed') {
        speechWanted = false;
      }
      emit({ type: 'speechError', message: String(ev.error) });
    };
    r.onend = () => {
      // Auto-restart so the mic stays open, mirroring the native engine.
      if (speechWanted) setTimeout(startRecog, 500);
    };
    try {
      r.start();
    } catch {
      /* already started */
    }
  }

  // ── Location + derived heading ─────────────────────────────────────────
  let watchId: number | null = null;
  const history: { t: number; lat: number; lng: number }[] = [];

  function computeHeading(lat: number, lng: number, gpsHeading: number | null): number {
    const now = Date.now();
    history.push({ t: now, lat, lng });
    while (history.length && now - history[0].t > HEADING_HISTORY_MS) history.shift();
    // Walk backwards until we've covered HEADING_DISTANCE_M of travel.
    for (let i = history.length - 2; i >= 0; i--) {
      const p = history[i];
      if (distM(p.lat, p.lng, lat, lng) >= HEADING_DISTANCE_M) {
        return bearingDeg(p.lat, p.lng, lat, lng);
      }
    }
    return gpsHeading != null && !Number.isNaN(gpsHeading) ? gpsHeading : 0;
  }

  // ── Keep-awake (Screen Wake Lock) ──────────────────────────────────────
  const wakeLockApi = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  let wakeLock: WakeLockSentinelLike | null = null;
  async function acquireWake() {
    if (!wakeLockApi) return;
    try {
      wakeLock = await wakeLockApi.request('screen');
      wakeLock.addEventListener('release', () => {
        wakeLock = null;
      });
    } catch {
      /* denied */
    }
  }

  return {
    capabilities: () => caps,
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    startSpeech(words) {
      grammar = words.map((w) => w.toLowerCase());
      if (!SR) return;
      readyEmitted = false;
      speechWanted = true;
      if (!recog) startRecog();
    },
    stopSpeech() {
      speechWanted = false;
      readyEmitted = false;
      try {
        recog?.stop();
      } catch {
        /* noop */
      }
      recog = null;
    },
    // The PWA shell can't introspect the audio route, so this is a no-op;
    // browser speech recognition isn't the primary target anyway.
    setAudioPlaying(_playing) {
      /* noop */
    },
    getAudioDebug() {
      return "";
    },
    startLocation() {
      if (!geo || watchId != null) return;
      watchId = geo.watchPosition(
        (pos) => {
          const c = pos.coords;
          const fix: GeoFix = {
            lat: c.latitude,
            lng: c.longitude,
            accuracy: c.accuracy ?? 0,
            speed: c.speed ?? 0,
            heading: computeHeading(c.latitude, c.longitude, c.heading),
          };
          emit({ type: 'location', fix });
        },
        (err) => emit({ type: 'locationError', message: err.message }),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
      );
    },
    stopLocation() {
      if (watchId != null) {
        geo.clearWatch(watchId);
        watchId = null;
      }
      history.length = 0;
    },
    setKeepAwake(on) {
      if (on) void acquireWake();
      else {
        wakeLock?.release?.();
        wakeLock = null;
      }
    },
    // Snapshot localStorage synchronously at startup (see NativeShell.storage).
    storage: readLocalStorage(),
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {
        /* noop */
      }
    },
    loadApp: (_url) => {
      // In a browser there's only the served version; just inform the user.
      showPwaToast('Browser always runs the server-served version.');
    },
    showToast: (message) => showPwaToast(message),
  };
}

/** Read all of localStorage into a plain object (best-effort, never throws). */
function readLocalStorage(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k == null) continue;
      const v = localStorage.getItem(k);
      if (v != null) out[k] = v;
    }
  } catch {
    /* storage blocked — return what we have */
  }
  return out;
}

/** Minimal transient toast for the PWA fallback. */
function showPwaToast(message: string): void {
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText =
    'position:fixed;left:50%;bottom:2rem;transform:translateX(-50%);' +
    'background:#222;color:#fff;padding:0.6rem 1rem;border-radius:8px;' +
    'font:14px system-ui,sans-serif;z-index:9999;opacity:0;transition:opacity .2s;max-width:80vw;text-align:center';
  document.body.appendChild(el);
  requestAnimationFrame(() => (el.style.opacity = '1'));
  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 300);
  }, 2500);
}
